import {
  Box,
  Card,
  Link,
  TogglerCheckbox,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { ProcessFragment } from 'generated/resources/graphql'

import Selector from 'modules/core/components/Selector'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { TrainingWizardData } from 'modules/trainings/components/Revamp/NewTrainingWizard/types'

type Props = {
  fundaeToggleStatus: boolean
  workflowStatus: boolean
  processId?: string
  onChange(data: Partial<TrainingWizardData>): void
  setOpen?: (value: boolean) => void
  processes: ProcessFragment[]
  validationError?: string
  defaultFundaeWorkflow?: string
  from: 'creation' | 'edition'
}

export function WorkflowToggle({
  workflowStatus,
  processId,
  onChange,
  setOpen,
  processes,
  validationError,
  defaultFundaeWorkflow,
  from,
}: Props) {
  const [isTooltipTimeOpen, toggleTooltipTime] = useToggle(false)
  const toggleDisabled = processes.length === 0

  const onOpen = (value: boolean) => {
    if (setOpen) setOpen(value)
    onChange({
      workflowStatus: value,
      processId: value ? processId : undefined,
    })

    if (from === 'creation') track(trackingEvents.training.trainingWorkflows.linkWorkflowCreation)
    if (from === 'edition') track(trackingEvents.training.trainingWorkflows.linkWorkflowEdition)
  }
  useEffect(() => {
    if (defaultFundaeWorkflow && !processId) {
      onChange({ processId: defaultFundaeWorkflow })
    }
  }, [defaultFundaeWorkflow, processId])

  return (
    <Card>
      <Card.Title
        size="small"
        action={
          <Box onMouseEnter={toggleTooltipTime(true)} onMouseLeave={toggleTooltipTime(false)}>
            {toggleDisabled ? (
              <Tooltip.Alt
                open={isTooltipTimeOpen}
                attachment="top center"
                label={i18n.t('trainings.workflows.toggle.popover.blank')}
              >
                <TogglerCheckbox
                  disabled={toggleDisabled}
                  onChange={(value) => onOpen(value)}
                  value={workflowStatus}
                />
              </Tooltip.Alt>
            ) : (
              <TogglerCheckbox
                disabled={toggleDisabled}
                onChange={(value) => onOpen(value)}
                value={workflowStatus}
              />
            )}
          </Box>
        }
      >
        {i18n.t('trainings.workflows.toggle.card.title')}
      </Card.Title>
      <Card.Description>{i18n.t('trainings.workflows.toggle.card.description')}</Card.Description>
      <Card.Content>
        <Link.Href
          href="https://help.factorialhr.com/es_ES/formaciones/fundae"
          target="_blank"
          rel="noopener noreferrer"
        >
          {i18n.t('trainings.workflows.toggle.card.link')}
        </Link.Href>
      </Card.Content>
      <Card.ExpandableContent expanded={workflowStatus}>
        <Box gap="s16">
          <Selector.Popover
            blankLabel={i18n.t('trainings.workflows.toggle.popover.blank')}
            label={i18n.t('trainings.workflows.toggle.popover.label')}
            mandatory
            onChange={(value) => {
              onChange({ processId: String(value) })
            }}
            placeholder={i18n.t('trainings.workflows.toggle.popover.placeholder')}
            value={[processId]}
            error={validationError}
          >
            {processes.map((x) => (
              <Selector.Option key={x.id} label={x.name} searchValue={x.name} value={x.id}>
                {x.name}
              </Selector.Option>
            ))}
          </Selector.Popover>
        </Box>
      </Card.ExpandableContent>
    </Card>
  )
}
